import { Feature, FeatureCollection, Geometry } from "geojson";

import { ALL_COLORS, COLORS } from "@/constants/colors";
/**
 * Converts a DatasetResponse to a GeoJSON FeatureCollection.
 * Each row in the dataset becomes a feature, where the properties are mapped from the columns.
 * If attributesSchema is provided, it's used for mapping; otherwise, response.columns is used.
 */
import {
  CATCHMENT_GRID_ID_COLUMN_NAME,
  DATA_TYPE_VALUES,
  DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE,
  DEFAULT_SIMPLE_FILL_COLOR,
  DEFAULT_SIMPLE_OUTLINE_COLOR,
  MAX_STEP,
  MIN_STEP,
  ORDINAL_TYPES,
  ORDINAL_TYPES_COLUMN,
  SPATIAL_AGGREGATION_NAME,
  SPATIAL_ANALYSIS_RESULT_DATASET_NAME,
  SPATIAL_TYPE_TO_LAYER_TYPE
} from "@/constants/dataset";
import type { Timezone } from "@/constants/dateTime";
import { normalizeFilterValue } from "@/lib/utils";
import { defaultDataset } from "@/mocks/apis/dataset/data";
import { SpatialAggregationType } from "@/types/common";
import {
  AddUpdateRowsDatasetPayload,
  ATTRIBUTE_ID,
  ColorRanges,
  ColorScaleMethod,
  CreateNewDatasetType,
  CustomBreakStep,
  CustomBreaksValue,
  CustomOrdinalStep,
  CustomOrdinalValue,
  DataSchemaType,
  Dataset,
  DatasetAttributeSchema,
  DatasetData,
  DatasetDataColumn,
  DatasetDataRowV2,
  DatasetDataRowValue,
  DatasetDataType,
  DatasetMetadata,
  DatasetMetadataStream,
  DatasetsMap,
  EColorLabel,
  EColorNames,
  H3Configuration,
  SchemaToDatasetMap,
  SimpleFillValue,
  TransformedProperty
} from "@/types/dataset";
import { SelectedFeature } from "@/types/map";
import { CatchmentDatasetConfig } from "@/types/spatial";
import { isDatetime, translateToUserTimezone } from "@/utils/date";

import { area } from "@turf/turf";

import { processValue } from "./attributeProcessing";
import { getColorScaleMap } from "./color";
import { handleCategoricalValues, handleNumericValues } from "./common";
import { convertFormatterDayjs } from "./date";
import { formatNumber } from "./number";
import { computeStepValue } from "./stepValue";
import { formatRenderValue } from "./string";
import { isWKTPoint, wktToGeoJSON } from "./wkt";

interface ProcessedRowData {
  properties: Record<string, string | number | boolean | null | undefined>;
  baseId: number | string | undefined;
  collectedGeometries: Array<{
    geometry: Geometry;
    sourceColumnName: string;
    columnIndex: number;
  }>;
}
interface ResolvedColumn {
  info: DatasetAttributeSchema | DatasetDataColumn | undefined;
  isVisible: boolean;
  isEnhancedGeo: boolean;
}

const getColumnName = (
  columnInfo: DatasetAttributeSchema | DatasetDataColumn,
  useAttributesSchema: boolean
) =>
  useAttributesSchema
    ? (columnInfo as DatasetAttributeSchema).attribute_name
    : (columnInfo as DatasetDataColumn).name;

const getIsReferenceId = (
  columnInfo: DatasetAttributeSchema | DatasetDataColumn,
  columnName: string,
  useAttributesSchema: boolean
) =>
  useAttributesSchema
    ? (columnInfo as DatasetAttributeSchema).is_reference_id ||
      columnName === "id"
    : columnName === "id";

const handleReferenceId = (
  value: DatasetDataRowValue,
  columnName: string,
  columnIndex: number,
  processedData: ProcessedRowData
) => {
  let idValue: string | number | undefined;

  if (typeof value === "string" || typeof value === "number") {
    idValue = value;
  } else if (value !== null && value !== undefined) {
    idValue = String(idValue);
  }

  processedData.baseId = idValue ?? columnIndex.toString();
  processedData.properties[columnName] = processedData.baseId;
};

const handleGeometryColumn = (
  value: string,
  columnInfo: DatasetAttributeSchema | DatasetDataColumn,
  columnName: string,
  columnIndex: number,
  processedData: ProcessedRowData,
  spatialAggregationType?: SpatialAggregationType
) => {
  // Skip non-point geometries when spatial aggregation type is 'poi'
  if (spatialAggregationType === "poi" && !isWKTPoint(value)) {
    return;
  }

  if (
    spatialAggregationType === SPATIAL_AGGREGATION_NAME.H3 ||
    spatialAggregationType === SPATIAL_AGGREGATION_NAME.geohash.toLowerCase()
  ) {
    processedData.properties[columnName] = value;
    return;
  }

  // add geometry value to properties for Line and Polygon types, so it can be used in and feature info
  if (
    spatialAggregationType === SPATIAL_AGGREGATION_NAME.line.toLowerCase() ||
    spatialAggregationType === SPATIAL_AGGREGATION_NAME.polygon.toLowerCase()
  ) {
    processValue(value, columnInfo, processedData.properties);
  }

  const geometry = wktToGeoJSON(value)?.geometry;
  if (geometry) {
    processedData.collectedGeometries.push({
      geometry,
      sourceColumnName: columnName,
      columnIndex
    });
  }
};

const processRowValue = (
  value: DatasetDataRowValue,
  columnInfo: DatasetAttributeSchema | DatasetDataColumn | undefined,
  useAttributesSchema: boolean,
  columnIndex: number,
  processedData: ProcessedRowData,
  spatialAggregationType?: SpatialAggregationType
) => {
  if (!columnInfo) {
    return;
  }

  const columnName = getColumnName(columnInfo, useAttributesSchema);
  const isReferenceId = getIsReferenceId(
    columnInfo,
    columnName,
    useAttributesSchema
  );

  if (isReferenceId) {
    handleReferenceId(value, columnName, columnIndex, processedData);
    return;
  }

  if (columnInfo.is_reference_geo) {
    if (typeof value === "string" && value) {
      handleGeometryColumn(
        value,
        columnInfo,
        columnName,
        columnIndex,
        processedData,
        spatialAggregationType
      );
    }
    return;
  }

  processValue(
    value,
    columnInfo as DatasetDataColumn,
    processedData.properties
  );
};

const createFeaturesFromRowData = (
  processedData: ProcessedRowData,
  rowIndex: number,
  features: Array<Feature<Geometry>>
) => {
  const { baseId, collectedGeometries, properties } = processedData;

  if (collectedGeometries.length > 0) {
    collectedGeometries.forEach(
      ({ geometry, sourceColumnName, columnIndex }, geometryIndex) => {
        const featureId =
          baseId !== undefined
            ? `${baseId}-${geometryIndex}`
            : `${rowIndex}-${columnIndex}-${geometryIndex}`;

        features.push({
          type: "Feature",
          id: featureId,
          geometry: geometry,
          properties: {
            ...properties,
            ["Geometry Source"]: sourceColumnName
          }
        });
      }
    );
  } else {
    const featureId = baseId ?? rowIndex.toString();
    features.push({
      type: "Feature",
      id: featureId,
      geometry: null as unknown as Geometry,
      properties: properties
    });
  }
};

/**
 * Builds two lookup maps from an `attributesSchema` array for efficient
 * column-to-schema matching.
 *
 * Two maps are produced:
 * - `schemaByName`     – keyed by `attribute_name` (1-to-1, last-write wins
 *                        when names collide).
 * - `schemaBySchemaId` – keyed by `id`, storing an **array** of entries
 *                        because multiple admin-area attributes can share the
 *                        same `id` (e.g. Village Name / Village Code both
 *                        reference `id: "ae95ab68-…"`).
 *
 * @param attributesSchema - The dataset schema entries returned by the API.
 * @returns An object containing both lookup maps.
 */
const buildSchemaLookupMaps = (
  attributesSchema: DatasetAttributeSchema[]
): {
  schemaByName: Map<string, DatasetAttributeSchema>;
  schemaBySchemaId: Map<string, DatasetAttributeSchema[]>;
} => {
  const schemaByName = new Map<string, DatasetAttributeSchema>();
  const schemaBySchemaId = new Map<string, DatasetAttributeSchema[]>();

  for (const attr of attributesSchema) {
    if (attr.attribute_name) {
      schemaByName.set(attr.attribute_name, attr);
    }
    if (attr.id) {
      const existing = schemaBySchemaId.get(attr.id);
      if (existing) {
        existing.push(attr);
      } else {
        schemaBySchemaId.set(attr.id, [attr]);
      }
    }
  }

  return { schemaByName, schemaBySchemaId };
};

/**
 * Attempts to find a matching `DatasetAttributeSchema` for a column by
 * comparing `col.schema_id` against the schema `id` lookup map.
 *
 * This is the **Priority 2 fallback** used when name-based matching fails
 * (e.g. a column was renamed in the UI but still carries the original
 * `schema_id`).
 *
 * When multiple schema entries share the same `id` (duplicate `schema_id`),
 * the first entry whose `position` has **not yet been claimed** is returned.
 * This prevents two columns from incorrectly resolving to the same schema row.
 *
 * @param col             - The dataset column to find a match for.
 * @param schemaBySchemaId - Lookup map of schema `id` → schema entries (may contain duplicates).
 * @param usedPositions   - Set of schema `position` values already claimed by earlier columns.
 * @returns The matched schema entry, or `undefined` if no match is found.
 */
const findBySchemaId = (
  col: DatasetDataColumn,
  schemaBySchemaId: Map<string, DatasetAttributeSchema[]>,
  usedPositions: Set<number>
): DatasetAttributeSchema | undefined => {
  if (!col.schema_id) return undefined;
  const candidates = schemaBySchemaId.get(col.schema_id);
  return candidates?.find((c) => !usedPositions.has(c.position));
};

/**
 * Creates a mapping from each **column index** (matching the row data array)
 * to its corresponding `DatasetAttributeSchema` entry.
 *
 * Matching strategy (per column, in priority order):
 * 1. **Name match** – `col.name === schema.attribute_name` (most reliable).
 * 2. **Schema-ID match** – `col.schema_id === schema.id` (handles renamed columns).
 *
 * Columns that exist in `data.rows` but have **no corresponding schema entry**
 * (e.g. `orbis_li_id`) are simply omitted from the map, so they will be
 * skipped during row processing without shifting the indices of other columns.
 *
 * A `usedSchemaPositions` set prevents the same schema entry from being
 * claimed by more than one column when `schema_id` values are duplicated
 * across admin-area attributes.
 *
 * @param columns          - The `columns` array from the dataset response.
 * @param attributesSchema - The full schema array from the API.
 * @returns A map of `columnIndex → DatasetAttributeSchema`.
 */
const matchColumnsToSchema = (
  columns: DatasetDataColumn[],
  attributesSchema: DatasetAttributeSchema[]
): Map<number, DatasetAttributeSchema> => {
  const schemaByColumnIndex = new Map<number, DatasetAttributeSchema>();
  const { schemaByName, schemaBySchemaId } =
    buildSchemaLookupMaps(attributesSchema);
  const usedSchemaPositions = new Set<number>();

  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    const matched =
      schemaByName.get(col.name) ??
      findBySchemaId(col, schemaBySchemaId, usedSchemaPositions);

    if (matched) {
      usedSchemaPositions.add(matched.position);
      schemaByColumnIndex.set(i, matched);
    }
  }

  return schemaByColumnIndex;
};

/**
 * Resolves the metadata for a single column when `attributesSchema` is being used.
 *
 * - If the schema entry is missing or has `is_visible: false`, the column is
 *   marked as not visible and will be skipped during row processing.
 * - `isEnhancedGeo` is pre-computed here (once per column, not per row) to
 *   avoid redundant checks inside the hot row loop. It is `true` when either
 *   `schema.is_reference_geo` **or** `col.is_reference_geo` is set.
 *
 * Note: we intentionally do NOT fall back to `col.data_type === TYPE_GEOMETRY`
 * because some datasets contain geometry columns that are deliberately excluded
 * from the map (e.g. secondary/unused geometry columns where both schema and
 * column have `is_reference_geo: false`). Checking `data_type` would force
 * those unwanted geometries onto the map.
 *
 * @param col    - The column definition from the dataset response.
 * @param schema - The matched schema entry (may be `undefined` if no match was found).
 * @returns A `ResolvedColumn` with pre-computed visibility and geometry flags.
 */
const resolveColumnWithSchema = (
  col: DatasetDataColumn,
  schema: DatasetAttributeSchema | undefined
): ResolvedColumn => {
  if (!schema?.is_visible) {
    return { info: undefined, isVisible: false, isEnhancedGeo: false };
  }

  // Only trust the explicit is_reference_geo flag from either source.
  // Do NOT fall back to data_type check — it would incorrectly promote
  // unused geometry columns (where is_reference_geo is intentionally false).
  const isEnhancedGeo = schema.is_reference_geo || col?.is_reference_geo;

  return { info: schema, isVisible: true, isEnhancedGeo };
};

/**
 * Pre-computes an array of `ResolvedColumn` metadata — one entry per column index.
 *
 * This runs **once before the row loop**, so that the inner per-cell loop only
 * needs a cheap array access and boolean check instead of repeated `Map.get()`
 * lookups, visibility checks, and geometry-type comparisons on every cell.
 *
 * When `useAttributesSchema` is `false`, each column is resolved directly from
 * the `columns` array with no schema involvement.
 *
 * @param columns              - The `columns` array from the dataset response.
 * @param schemaByColumnIndex  - The column-index-to-schema map built by `matchColumnsToSchema`.
 * @param useAttributesSchema  - Whether an `attributesSchema` was provided.
 * @returns An array aligned with the column indices, each containing pre-resolved metadata.
 */
const preComputeColumnMetadata = (
  columns: DatasetDataColumn[],
  schemaByColumnIndex: Map<number, DatasetAttributeSchema>,
  useAttributesSchema: boolean
): ResolvedColumn[] => {
  return columns.map((col, i) => {
    if (!useAttributesSchema) {
      return {
        info: col,
        isVisible: !!col?.is_visible,
        isEnhancedGeo: col.is_reference_geo
      };
    }
    return resolveColumnWithSchema(col, schemaByColumnIndex.get(i));
  });
};

/**
 * Processes a single data row, delegating each cell to `processRowValue` and
 * then converting the accumulated result into GeoJSON Feature(s) via
 * `createFeaturesFromRowData`.
 *
 * For each cell in the row:
 * - Non-visible or unmatched columns are skipped via the pre-computed
 *   `resolvedColumns` metadata (no runtime type checks needed).
 * - Geometry columns that were flagged as `isEnhancedGeo` during pre-computation
 *   receive a shallow-copied `columnInfo` with `is_reference_geo: true`, ensuring
 *   `processRowValue` correctly identifies them as geometry. The spread only
 *   happens for geometry columns (typically 1–2 per row), not for every cell.
 *
 * @param rowData               - The raw row values array.
 * @param rowIndex              - The index of this row in the dataset (used for feature ID fallback).
 * @param resolvedColumns       - Pre-computed column metadata array.
 * @param useAttributesSchema   - Whether an `attributesSchema` was provided.
 * @param spatialAggregationType - The dataset's spatial type (e.g. "poi", "adm_area", "h3").
 * @param features              - The output array; new Feature(s) are pushed directly into it.
 */
const processRow = (
  rowData: DatasetDataRowV2,
  rowIndex: number,
  resolvedColumns: ResolvedColumn[],
  useAttributesSchema: boolean,
  spatialAggregationType: SpatialAggregationType,
  features: Array<Feature<Geometry>>
): void => {
  const processedData: ProcessedRowData = {
    properties: {},
    baseId: undefined,
    collectedGeometries: []
  };

  for (let columnIndex = 0; columnIndex < rowData.length; columnIndex++) {
    const resolved = resolvedColumns[columnIndex];
    if (!resolved?.isVisible || !resolved.info) continue;

    const columnInfo =
      useAttributesSchema && resolved.isEnhancedGeo
        ? { ...resolved.info, is_reference_geo: true }
        : resolved.info;

    processRowValue(
      rowData[columnIndex],
      columnInfo,
      useAttributesSchema,
      columnIndex,
      processedData,
      spatialAggregationType
    );
  }

  createFeaturesFromRowData(processedData, rowIndex, features);
};

/**
 * Converts a `DatasetData` response into a GeoJSON `FeatureCollection`.
 *
 * Each row in the dataset becomes one or more GeoJSON Features, where
 * properties are mapped from columns and geometry is parsed from WKT strings.
 *
 * **Schema matching strategy** (when `attributesSchema` is provided):
 *
 * The `columns` array (from the API response) and `attributesSchema` may not
 * be aligned by index — for example, columns can contain internal fields like
 * `orbis_li_id` that have no corresponding schema entry, causing all subsequent
 * indices to shift. To solve this, columns are matched to schema entries by:
 *
 * 1. **Name** – `col.name === schema.attribute_name` (primary, most reliable).
 * 2. **Schema ID** – `col.schema_id === schema.id` (fallback for renamed columns).
 *
 * **Geometry detection**:
 *
 * A column is treated as geometry only when `is_reference_geo` is `true` in
 * either the matched schema entry or the column definition. The `data_type`
 * field is intentionally **not** used as a fallback because some datasets
 * contain secondary geometry columns that are deliberately excluded from
 * the map (`is_reference_geo: false` in both sources).
 *
 * **Performance optimizations**:
 *
 * - Column metadata (visibility, geometry flag) is pre-computed **once** before
 *   the row loop, avoiding repeated Map lookups and type checks per cell.
 * - Object spread for enhanced geometry info only occurs on actual geometry
 *   columns (typically 1–2), not on every cell in every row.
 *
 * @param datasetData       - The dataset response containing columns, rows, and spatial type.
 * @param attributesSchema  - Optional schema array for richer column metadata.
 *                            When omitted, `datasetData.columns` is used directly.
 * @returns A GeoJSON `FeatureCollection` with one Feature per row (or per geometry when
 *          a row contains multiple geometry columns).
 *
 * @example
 * ```typescript
 * // Without schema — uses columns directly
 * const fc = convertDatasetResponseToFeatureCollection(datasetData);
 *
 * // With schema — uses name/schema_id matching + enhanced geo detection
 * const fc = convertDatasetResponseToFeatureCollection(datasetData, schema);
 * ```
 */
export const convertDatasetResponseToFeatureCollection = (
  datasetData: DatasetData,
  attributesSchema?: DatasetAttributeSchema[]
): FeatureCollection<Geometry> => {
  if (!datasetData) return { type: "FeatureCollection", features: [] };

  const { columns, rows, spatial_aggregation_type } = datasetData;
  const useAttributesSchema = !!attributesSchema;
  const features: Array<Feature<Geometry>> = [];

  const schemaByColumnIndex =
    useAttributesSchema && attributesSchema
      ? matchColumnsToSchema(columns, attributesSchema)
      : new Map<number, DatasetAttributeSchema>();

  const resolvedColumns = preComputeColumnMetadata(
    columns,
    schemaByColumnIndex,
    useAttributesSchema
  );

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    processRow(
      rows[rowIndex],
      rowIndex,
      resolvedColumns,
      useAttributesSchema,
      spatial_aggregation_type,
      features
    );
  }

  return { type: "FeatureCollection", features };
};

/**
 * Gets all unique property keys from feature properties
 */
export const getCategoriesFromDatasetReponse = (
  response: DatasetData
): {
  type: DatasetDataType;
  value: string;
}[] => {
  const categories: {
    type: DatasetDataType;
    value: string;
  }[] = [];

  response.columns.forEach((column) => {
    if (
      column.name === "geom" ||
      column.name === "id" ||
      CATCHMENT_GRID_ID_COLUMN_NAME.includes(column.name)
    )
      return;

    categories.push({
      type: column.data_type,
      value: column.name
    });
  });

  return categories;
};

/**
 * Calculates color distribution ranges for feature properties based on their type.
 * For string values, it creates color categories.
 * For numeric values, it creates quantile ranges.
 */
export const calculateFeaturePropertyColorRanges = (
  featureCollection: FeatureCollection<Geometry>,
  propertyName: string,
  isOrdinal: boolean,
  step: number
) => {
  // Extract property values
  const propertyValues = featureCollection.features.map(
    (f) => f.properties?.[propertyName]
  );

  return isOrdinal
    ? handleCategoricalValues(propertyValues, step)
    : handleNumericValues(propertyValues, step);
};

/*
 * Transforms properties of a selected feature into a more structured format
 * by associating them with their respective categories.
 * This is useful for displaying properties in a categorized manner.
 */

export const transformPropertiesWithCategory = (
  properties: SelectedFeature,
  categories: DatasetAttributeSchema[],
  timezone?: Timezone
): TransformedProperty[] => {
  return Object.entries(properties).reduce<TransformedProperty[]>(
    (acc, [key, value]) => {
      const category = categories.find((cat) => cat.attribute_name === key);
      if (category && isDatetime(category.data_type)) {
        const datasetDataType = SchemaToDatasetMap[category.data_type];
        const displayValue = translateToUserTimezone(
          timezone,
          datasetDataType,
          value
        );

        acc.push({
          key,
          value: displayValue,
          data_type: category.data_type,
          data_label: category.data_label
        });
      }
      return acc;
    },
    []
  );
};

/**
 * Transforms an array of properties into a key-value pair object.
 * Each property is formatted based on its data type using the `formatRenderValue` function.
 * This transformation is useful for efficiently accessing attributes by their keys
 * and displaying them in a structured format.
 */
export const transformVisibleAttributes = (
  transformProperty: TransformedProperty[],
  timezone?: Timezone
): Record<string, string> => {
  return transformProperty.reduce<Record<string, string>>((acc, item) => {
    const safeValue = item.value ?? "";
    const formattedValue = formatRenderValue({
      type: item.data_type,
      value: safeValue,
      timezone: timezone
    });
    acc[item.key] = String(formattedValue);
    return acc;
  }, {});
};

/** Function to return only the available/not deleted `datasets` from the given `datasets` slice */
export const getAvailableDatasets = (datasets: DatasetsMap): DatasetsMap => {
  return Object.fromEntries(
    Object.entries(datasets).filter(([, dataset]) => !!dataset.data.available)
  );
};

/**
 * Defines matching rules for selecting a default attribute based on spatial aggregation type.
 *
 * Each rule specifies:
 * - @param spatialType: The spatial aggregation type this rule applies to (e.g. H3, POI, polygon)
 * - @param dataType: The expected data type of the matching attribute
 * - @param isMatchingAttribute: A predicate function to further validate the attribute by name/column
 *
 * Rules are evaluated in order and used by `getDefaultSelectedCategory` as priority step 2,
 * after checking for a "score" attribute.
 *
 * Current rules:
 * - H3       → First INT attribute whose name includes "count"
 * - POI      → First TEXT attribute where both name and column equal "name"
 * - Polygon  → First TEXT attribute where both name and column equal "class"
 */
const SPATIAL_AGGREGATION_ATTRIBUTE_BY_TYPE: Array<{
  spatialType: string;
  dataType: DataSchemaType;
  isMatchingAttribute: (attr: DatasetAttributeSchema) => boolean;
}> = [
  {
    spatialType: SPATIAL_TYPE_TO_LAYER_TYPE.H3,
    dataType: DataSchemaType.INT,
    isMatchingAttribute: (attr) =>
      !!attr.attribute_name?.toLowerCase().includes("count")
  },
  {
    spatialType: SPATIAL_TYPE_TO_LAYER_TYPE.poi.toLowerCase(),
    dataType: DataSchemaType.TEXT,
    isMatchingAttribute: (attr) =>
      attr.attribute_name?.toLowerCase() === "name" &&
      attr.attribute_column?.toLowerCase() === "name"
  },
  {
    spatialType: SPATIAL_TYPE_TO_LAYER_TYPE.polygon.toLowerCase(),
    dataType: DataSchemaType.TEXT,
    isMatchingAttribute: (attr) =>
      attr.attribute_name?.toLowerCase() === "class" &&
      attr.attribute_column?.toLowerCase() === "class"
  }
];

/**
 * Finds a suitable attribute from the dataset's attributes based on the spatial aggregation type.
 * This function is used as part of the logic to determine the default selected category for a dataset.
 * It checks the dataset's attributes against predefined rules for specific spatial types (e.g., H3, POI, Polygon) to find an attribute that matches the expected data type and naming conventions.
 * @param attributes - The array of dataset attribute schemas to search through.
 * @param spatialAggregationType - The spatial aggregation type of the dataset, which influences the selection criteria for the attribute.
 * @returns The matching attribute schema if found, otherwise undefined.
 **/
const findSpatialAggregationTypeAttribute = (
  attributes: DatasetAttributeSchema[],
  spatialAggregationType?: SpatialAggregationType
): DatasetAttributeSchema | undefined => {
  const matchedRule = SPATIAL_AGGREGATION_ATTRIBUTE_BY_TYPE.find(
    ({ spatialType }) => spatialType === spatialAggregationType
  );
  return matchedRule
    ? attributes.find(
        (attr) =>
          attr.data_type === matchedRule.dataType &&
          matchedRule.isMatchingAttribute(attr)
      )
    : undefined;
};

/**
 * Selects the most appropriate attribute to use as the default category for dataset visualization.
 *
 * The selection follows a priority order:
 * 1. Prefers the "score" attribute if it exists (case-insensitive)
 * 2. Otherwise, selects the first non-special, non-geometry attribute
 * 3. Falls back to the first attribute in the array
 * 4. Returns undefined if the array is empty
 *
 * @param attributes - Array of dataset attribute schemas to choose from
 * @param spatialAggregationType - The spatial aggregation type of the dataset, which may influence attribute selection for certain types (e.g. H3, POI)
 * @returns The selected attribute schema that should be used as the default category, or undefined if no attributes exist
 *
 * @example
 * ```typescript
 * const attributes = [
 *   { attribute_name: 'id', data_type: DataSchemaType.STRING },
 *   { attribute_name: 'score', data_type: DataSchemaType.NUMERIC },
 *   { attribute_name: 'geometry', data_type: DataSchemaType.GEOMETRY }
 * ];
 *
 * const defaultCategory = getDefaultSelectedCategory(attributes, SPATIAL_AGGREGATION_NAME.H3);
 * // Returns the 'score' attribute
 * ```
 */
export const getDefaultSelectedCategory = (
  attributes: DatasetAttributeSchema[],
  spatialAggregationType?: SpatialAggregationType
): DatasetAttributeSchema | undefined => {
  if (attributes.length === 0) return undefined;

  // 1. Prefer the "score" attribute if it exists
  const scoreAttribute = attributes.find(
    (attr) => attr.attribute_name?.toLowerCase() === "score"
  );
  if (scoreAttribute) return scoreAttribute;

  // 2. Match by spatial type rules (H3 → count, POI → name, polygon → class)
  const spatialAggregationTypeAttribute = findSpatialAggregationTypeAttribute(
    attributes,
    spatialAggregationType
  );
  if (spatialAggregationTypeAttribute) return spatialAggregationTypeAttribute;

  // 3. Otherwise, prefer the first non-special, non-geometry attribute
  const regularAttribute = attributes.find(
    (attr) =>
      !(attr.attribute_name in ATTRIBUTE_ID) &&
      attr.data_type !== DataSchemaType.GEOMETRY
  );
  if (regularAttribute) return regularAttribute;

  // 4. Fallback: first attribute
  return attributes[0];
};

/**
 * Determines visibility based on whether it's an analysis result and the dataset name.
 * Only the "spatial analysis result" dataset is visible by default.
 */
export const getDatasetVisibility = (
  isAnalysisResult: boolean,
  datasetName: string | undefined
): { isVisible: boolean; visibility: "visible" | "none" } => {
  const shouldBeVisible =
    isAnalysisResult && datasetName === SPATIAL_ANALYSIS_RESULT_DATASET_NAME;
  return {
    isVisible: shouldBeVisible,
    visibility: shouldBeVisible ? "visible" : "none"
  };
};

/**
 * Creates the default dataset style configuration.
 */
const createDefaultDatasetStyle = (
  defaultCategory: DatasetAttributeSchema | undefined,
  visibility: "visible" | "none"
): Dataset["style"] => ({
  ...defaultDataset.style,
  visibility,
  labelConfig: {
    ...defaultDataset.style.labelConfig,
    attribute_id: defaultCategory?.id ?? "",
    attribute_name: defaultCategory?.attribute_name ?? ""
  },
  stroke: {
    ...defaultDataset.style.stroke,
    attribute_id: defaultCategory?.id ?? "",
    attribute_name: defaultCategory?.attribute_name ?? ""
  }
});

/**
 * Creates a default spatial dataset object with configured properties, styles, and visibility settings.
 *
 * This function constructs a Dataset object from raw dataset data and attributes, applying
 * default configurations and determining visibility based on whether it's an analysis result
 * and the spatial aggregation type.
 *
 * @param {Object} params - The parameters object
 * @param {DatasetData} params.data - The raw dataset data containing id, name, spatial type, and rows
 * @param {DatasetAttributeSchema[]} params.attributes - Array of dataset attribute schemas
 * @param {boolean} [params.isAnalysisResult=false] - Whether this dataset is from an analysis result
 *
 * @returns {Dataset} A configured Dataset object with:
 *   - data: Basic dataset information (id, name, spatial_aggregation_type, total_rows)
 *   - properties: Visibility and fetching state based on analysis result and spatial type
 *   - style: Visibility styling ("visible" or "none") based on spatial aggregation type
 *   - categories: The provided attributes array
 *   - selectedCategory: Default selected category from attributes
 *
 * @example
 * ```typescript
 * const dataset = createDefaultSpatialDataset({
 *   data: {
 *     dataset_id: "123",
 *     name: "Population Data",
 *     spatial_aggregation_type: EOutputAnalysisType.GRID,
 *     rows: [...]
 *   },
 *   attributes: [...],
 *   isAnalysisResult: true
 * });
 * ```
 */
export const createDefaultSpatialDataset = ({
  data,
  attributes,
  isAnalysisResult = false
}: {
  data: DatasetData;
  attributes: DatasetAttributeSchema[];
  isAnalysisResult?: boolean;
}): Dataset => {
  const defaultCategory = getDefaultSelectedCategory(
    attributes,
    data.spatial_aggregation_type
  );
  const { isVisible, visibility } = getDatasetVisibility(
    isAnalysisResult,
    data.name
  );

  return {
    data: {
      ...defaultDataset.data,
      id: data.dataset_id,
      name: data.name,
      display_name: data.display_name ?? data.name,
      spatial_aggregation_type: data.spatial_aggregation_type,
      total_rows: data.rows.length,
      short_description: "",
      dataset_size_kb: data.dataset_size_kb
    },
    properties: {
      isVisible,
      spatialType: data.spatial_aggregation_type,
      isFetching: !isAnalysisResult
    },
    style: createDefaultDatasetStyle(defaultCategory, visibility),
    categories: attributes,
    selectedCategory: defaultCategory ?? DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE
  };
};

export const createSpatialDatasetFromMetadata = ({
  data,
  attributes,
  isAnalysisResult = false,
  h3Configuration = null
}: {
  data: DatasetMetadataStream;
  attributes: DatasetAttributeSchema[];
  isAnalysisResult?: boolean;
  h3Configuration?: H3Configuration | null;
}): Dataset => {
  const defaultCategory = getDefaultSelectedCategory(
    attributes,
    data.spatial_aggregation_type
  );
  const { isVisible, visibility } = getDatasetVisibility(
    isAnalysisResult,
    data.name
  );

  return {
    data: {
      ...defaultDataset.data,
      id: data.dataset_id,
      name: data.name,
      display_name: data.name,
      spatial_aggregation_type: data.spatial_aggregation_type,
      total_rows: data.total_rows,
      short_description: "",
      dataset_size_kb: data.dataset_size_kb
    },
    properties: {
      isVisible,
      spatialType: data.spatial_aggregation_type,
      isFetching: false,
      gridConfig: data.grid_config
    },
    style: createDefaultDatasetStyle(defaultCategory, visibility),
    categories: attributes,
    selectedCategory: defaultCategory ?? DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE,
    h3Configuration
  };
};

/**
 * Checks if a dataset has a 'score' column and if all of its values are null.
 */
export const hasNoScore = (dataset: DatasetData): boolean => {
  const scoreColumnIndex = dataset.columns.findIndex(
    (column) => column.name.toLowerCase() === "score"
  );

  if (scoreColumnIndex === -1) return false;
  if (!dataset.rows?.length) return true;

  return dataset.rows.every((row) => {
    const value = row[scoreColumnIndex];

    if (value === null || value === undefined) return true;

    if (typeof value === "string") return value.trim().length === 0;

    if (typeof value === "object" && value !== null) {
      return Object.values(value).every((v) => {
        if (v === null || v === undefined) return true;
        if (typeof v === "string") return v.trim().length === 0;
        return false;
      });
    }
    return false;
  });
};

/**
 * Util function to check if a given data type is one of the ordinal types.
 * @param dataType - The data type to check against the ordinal types.
 * @returns {boolean} - Returns true if the data type is one of the ordinal types, false otherwise.
 */
export const isOrdinalType = (
  dataType: DataSchemaType | DatasetDataType
): boolean => {
  return (
    ORDINAL_TYPES.includes(dataType) || ORDINAL_TYPES_COLUMN.includes(dataType)
  );
};

/**
 * Derives the default Color Scale method from an attribute's data type:
 * ordinal (categorical) attributes get `"ordinal"`, numeric attributes get
 * `"quantile"`. Shared by dataset creation (`createNewDataset`) and the
 * "Color Based On" default in `DatasetItem` so both stay in sync.
 */
export const getDefaultColorScaleMethod = (
  dataType?: DataSchemaType
): ColorScaleMethod =>
  dataType && isOrdinalType(dataType) ? "ordinal" : "quantile";

export type ColorBasedOnValue = ColorScaleMethod;

type ColorScaleOption = {
  label: string;
  value: ColorScaleMethod;
  /** Which attribute data types this option is applicable to. */
  appliesTo: "numeric" | "non-numeric" | "all";
};

export const COLOR_BASED_ON_OPTIONS: ColorScaleOption[] = [
  { label: "Quantile", value: "quantile", appliesTo: "numeric" },
  { label: "Ordinal", value: "ordinal", appliesTo: "non-numeric" },
  { label: "Custom Breaks", value: "custom_breaks", appliesTo: "numeric" },
  { label: "Custom Ordinal", value: "custom_ordinal", appliesTo: "all" },
  { label: "Simple", value: "simple", appliesTo: "all" }
];

/**
 * Whether a given Color Scale option can be applied to an attribute of the
 * given data type. `isOrdinalType` returns false for numeric types
 * (int/float/percentage/ratio/lat/long) and true for everything else. When
 * `dataType` is omitted, every option is considered applicable.
 */
export const isColorBasedOnApplicable = (
  value: string,
  dataType?: DataSchemaType
): boolean => {
  const option = COLOR_BASED_ON_OPTIONS.find((o) => o.value === value);
  if (!option) return false;
  if (!dataType || option.appliesTo === "all") return true;
  const isNumeric = !isOrdinalType(dataType);
  return option.appliesTo === "numeric" ? isNumeric : !isNumeric;
};

/**
 * The Color Scale options applicable to the given attribute data type,
 * shaped as `{ label, value }` for the shared select component.
 */
export const getColorScaleOptions = (
  dataType?: DataSchemaType
): { label: string; value: string }[] =>
  COLOR_BASED_ON_OPTIONS.filter((option) =>
    isColorBasedOnApplicable(option.value, dataType)
  ).map(({ label, value }) => ({ label, value }));

/**
 * Default "Color Based On" (Color Scale) method, from the two values it needs —
 * the stored method and the attribute data type (not the whole dataset):
 *  1. `storedMethod`, when it still applies to the attribute type. New layers
 *     get it at creation (`createNewDataset` derives "simple" for POI,
 *     ordinal/quantile otherwise) and restored projects keep their saved value —
 *     so new POI correctly defaults to Simple via this branch.
 *  2. otherwise the attribute data type's default (ordinal vs quantile).
 *     Pre-feature projects have no stored method, so they land here and keep
 *     their existing Quantile/Ordinal visuals (PRD: backward compatibility —
 *     do NOT force POI to Simple here, that would mislabel existing projects).
 * Shared by the dataset list (`DatasetItem`) and Preview in Map so both agree.
 */
export const getColorBasedOnDefault = (
  storedMethod: ColorScaleMethod | undefined,
  dataType?: DataSchemaType
): ColorScaleMethod => {
  if (storedMethod && isColorBasedOnApplicable(storedMethod, dataType)) {
    return storedMethod;
  }
  return getDefaultColorScaleMethod(dataType);
};

/**
 * Default "Simple" Color Scale config (flat fill/outline + the attribute name
 * as the legend label). Used both when seeding a new POI dataset and when a
 * layer is switched to Simple without an existing config.
 */
export const getDefaultSimpleConfig = (label: string): SimpleFillValue => ({
  fillColor: DEFAULT_SIMPLE_FILL_COLOR,
  outlineColor: DEFAULT_SIMPLE_OUTLINE_COLOR,
  label,
  imageUrl: null,
  imageName: null,
  imageAssetId: null,
  iconId: null
});

/** Catch-all "Other" bucket colors for Custom Ordinal (data not assigned). */
export const CUSTOM_ORDINAL_OTHER_FILL_COLOR = "#6b7280";
export const CUSTOM_ORDINAL_OTHER_OUTLINE_COLOR = "#d1d5db";
// Absolute ceiling for Custom Ordinal category steps — matches the Color Scale
// step maximum. The EFFECTIVE cap tracks the active color scale step number
// (e.g. step 7 → at most 7 steps); this constant just bounds it. The auto-seed
// reserves the last slot for the user to add manually (step N → seeds N-1 rows
// when the data has >= N unique values), mirroring the Ordinal "top 9 + Others".
const CUSTOM_ORDINAL_MAX_STEPS = 10;

const clampCustomOrdinalStepCount = (stepCount: number): number =>
  Math.min(Math.max(stepCount, 1), CUSTOM_ORDINAL_MAX_STEPS);

const shouldReserveOtherSlot = (
  uniqueValuesLength: number,
  stepCount: number
): boolean =>
  uniqueValuesLength > stepCount ||
  uniqueValuesLength >= CUSTOM_ORDINAL_MAX_STEPS;

/**
 * Number of active Custom Ordinal category rows for a step count: `stepCount`
 * when the data fits, otherwise `stepCount - 1` (min 1) with the last slot reserved for "Other".
 */
export const getCustomOrdinalActiveStepCount = (
  uniqueValuesLength: number,
  stepCount: number
): number => {
  const count = clampCustomOrdinalStepCount(stepCount);
  return shouldReserveOtherSlot(uniqueValuesLength, count)
    ? Math.max(count - 1, 1)
    : count;
};

/**
 * Unique values of an attribute taken straight from the loaded tabular data
 * (the rows the data table view shows). Used to seed Custom Ordinal categories.
 */
export const getAttributeTableValues = (
  rawDataset: DatasetData | undefined,
  attributeName: string | undefined
): string[] => {
  if (!attributeName || !rawDataset?.columns) return [];
  const columnIndex = rawDataset.columns.findIndex(
    (col) => col.name === attributeName
  );
  if (columnIndex === -1) return [];
  const unique = new Set<string>();
  rawDataset.rows?.forEach((row) => {
    const cell = (row as DatasetDataRowValue[])[columnIndex];
    if (cell !== null && cell !== undefined && cell !== "") {
      unique.add(String(cell));
    }
  });
  return [...unique];
};

/**
 * Default "Custom Ordinal" config: one category per Color Scale step, each
 * seeded with the matching palette color (fill = outline), plus the catch-all
 * "Other" bucket. Values are auto-generated from the attribute's unique values
 * (one per category, like Ordinal), capped at the step count; the last slot is
 * reserved for the user to add manually, so values beyond the cap fall into
 * "Other" (step 10 → 9 seeded rows). A seeded category is named after its value;
 * empty/padded ones keep the generic "Category N" name. All names stay
 * renamable, and with no `uniqueValues` yet (data not loaded) categories are
 * empty.
 */
export const getDefaultCustomOrdinalConfig = (
  colorFamily: EColorNames,
  stepCount: number,
  uniqueValues: string[] = []
): CustomOrdinalValue => {
  const count = clampCustomOrdinalStepCount(stepCount);

  // Rows to seed: `count` when the data fits, otherwise `count - 1` with the
  // last slot reserved for "Other".
  const seedCount = getCustomOrdinalActiveStepCount(uniqueValues.length, count);

  // One category value per seeded row — the distinct data values ordered like
  // the Ordinal scale (`handleCategoricalValues`), capped at `seedCount`.
  let seededCategoryValues = handleCategoricalValues(
    uniqueValues,
    seedCount
  ).map((category) => category.category);

  // `handleCategoricalValues` drops "0"/" "; if that leaves nothing but the data
  // does have values, fall back to the raw distinct values so they still seed.
  if (!seededCategoryValues.length && uniqueValues.length) {
    seededCategoryValues = [...new Set(uniqueValues)].slice(0, seedCount);
  }

  // Row count follows the seeded categories (`seedCount` already reserved the
  // "Other" slot); with no data yet, keep `count` empty placeholder rows.
  const length =
    seededCategoryValues.length > 0 ? seededCategoryValues.length : count;
  // Size the palette at MIN_STEP minimum — the color scale map has no 1-step
  // entry, so a lone category would otherwise fall back to the grey "Other".
  const scale = getColorScaleMap(colorFamily, Math.max(length, MIN_STEP)) ?? [];
  const colorAt = (index: number) => {
    const key = scale[index] ?? scale[scale.length - 1];
    return COLORS[colorFamily]?.[key] ?? CUSTOM_ORDINAL_OTHER_OUTLINE_COLOR;
  };
  const steps: CustomOrdinalStep[] = Array.from({ length }, (_, index) => {
    const value = seededCategoryValues[index];
    const color = colorAt(index);
    return {
      // Seeded categories are named after their value (matches the Ordinal
      // legend); padded/empty ones fall back to the generic "Category N".
      label: value ?? `Category ${index + 1}`,
      values: value != null ? [value] : [],
      fillColor: color,
      outlineColor: color,
      imageUrl: null,
      imageName: null,
      imageAssetId: null,
      iconId: null
    };
  });
  return {
    steps,
    otherFillColor: CUSTOM_ORDINAL_OTHER_FILL_COLOR,
    otherOutlineColor: CUSTOM_ORDINAL_OTHER_OUTLINE_COLOR
  };
};

/**
 * Whether a Custom Ordinal config has any assigned values. When it doesn't, the
 * layer/legend should fall back to the default (ordinal) color scale rather than
 * paint everything as "Other" — Custom Ordinal only takes effect once the user
 * assigns values to a category and saves.
 */
export const hasCustomOrdinalAssignments = (
  config?: CustomOrdinalValue
): boolean => !!config?.steps?.some((step) => step.values.length > 0);

/**
 * Recolors a Custom Ordinal config's category fill/outline from a new Color
 * Scale palette (each step takes the palette color for its index; fill =
 * outline) AND resizes the step count to exactly `stepCount` (PRD Add/Remove
 * semantics): growing pads with empty "Category N" rows, shrinking trims
 * categories from the END. Categories that REMAIN are kept by position with
 * their labels/values/images; trimmed categories are dropped (their values are
 * NOT restored when the count grows again — re-added steps come back empty,
 * matching "Add Step"). The value lookup and "Other" colors are preserved.
 * Used when the palette or step count changes while Custom Ordinal is active.
 */
export const recolorCustomOrdinalConfig = (
  config: CustomOrdinalValue,
  colorFamily: EColorNames,
  stepCount: number
): CustomOrdinalValue => {
  const target = clampCustomOrdinalStepCount(stepCount);
  // Size the palette to at least MIN_STEP: COLOR_SCALE_MAP has no single-color
  // entry, so a 1-step config would otherwise get no scale and fall back to the
  // gray "Other" color. Sampling from a >= 2 scale keeps the single step on the
  // active color scale (its first color).
  const scale = getColorScaleMap(colorFamily, Math.max(target, MIN_STEP)) ?? [];
  const colorFor = (index: number) => {
    const key = scale[index] ?? scale[scale.length - 1];
    return COLORS[colorFamily]?.[key] ?? CUSTOM_ORDINAL_OTHER_OUTLINE_COLOR;
  };
  const steps: CustomOrdinalStep[] = Array.from(
    { length: target },
    (_, index) => {
      const existing = config.steps[index];
      const color = colorFor(index);
      if (existing) {
        return { ...existing, fillColor: color, outlineColor: color };
      }
      return {
        label: `Category ${index + 1}`,
        values: [],
        fillColor: color,
        outlineColor: color,
        imageUrl: null,
        imageName: null,
        imageAssetId: null,
        iconId: null
      };
    }
  );
  return { ...config, steps };
};

/**
 * Updates a Custom Ordinal config to the filtered data: keeps still-present values
 * and their groupings, and adds one step per still-unassigned value (capped).
 */
export const reconcileCustomOrdinalConfig = (
  config: CustomOrdinalValue,
  uniqueValues: string[],
  colorFamily: EColorNames
): CustomOrdinalValue => {
  const available = new Set(uniqueValues);
  // Keep each category's values that still exist; drop categories left empty.
  const survivingSteps = config.steps
    .map((step) => ({
      ...step,
      values: step.values.filter((value) => available.has(value))
    }))
    .filter((step) => step.values.length > 0);
  // Each still-unassigned value becomes its own step, so the step count follows
  // the data. Colors are placeholders — recolorCustomOrdinalConfig sets them below.
  const assigned = new Set(survivingSteps.flatMap((step) => step.values));
  const newSteps: CustomOrdinalStep[] = uniqueValues
    .filter((value) => !assigned.has(value))
    .map((value) => ({
      label: value,
      values: [value],
      fillColor: "",
      outlineColor: "",
      imageUrl: null,
      imageName: null,
      imageAssetId: null,
      iconId: null
    }));
  const steps = [...survivingSteps, ...newSteps];
  // Keep at least one (empty) category so the panel/legend always has a row.
  const finalSteps = steps.length
    ? steps
    : [{ ...config.steps[0], values: [] }];
  return recolorCustomOrdinalConfig(
    { ...config, steps: finalSteps },
    colorFamily,
    finalSteps.length
  );
};

/** Catch-all "Other" bucket colors for Custom Breaks (values in gaps/outside). */
export const CUSTOM_BREAKS_OTHER_FILL_COLOR = "#6b7280";
export const CUSTOM_BREAKS_OTHER_OUTLINE_COLOR = "#d1d5db";
export const CUSTOM_BREAKS_MAX_STEPS = 10;
export const CUSTOM_BREAKS_MIN_STEPS = 2;
/** Custom Breaks accepts at most 3 decimal places (PRD). */
export const CUSTOM_BREAKS_DECIMALS = 3;

/**
 * Rounds a break boundary. Integer attributes (e.g. a POI ID) round to whole
 * numbers — the ranges must never show decimals — while float attributes keep up
 * to `CUSTOM_BREAKS_DECIMALS` (3) decimal places.
 */
export const roundBreakValue = (
  value: number,
  isInteger: boolean = false
): number => {
  if (isInteger) return Math.round(value);
  const factor = 10 ** CUSTOM_BREAKS_DECIMALS;
  return Math.round(value * factor) / factor;
};

/** Whether a numeric attribute holds integers (no decimals allowed in breaks). */
export const isIntegerDataType = (dataType?: DataSchemaType): boolean =>
  dataType === DataSchemaType.INT;

/**
 * A palette color for the break at `index` (0 = the "Less"/min row) in a scale of
 * `total` steps. `COLOR_SCALE_MAP` is ordered dark→light by index. When
 * `reverse` is set the index is flipped so the min row takes the LIGHTEST shade
 * and the max row the darkest; otherwise the min row takes the DARKEST shade.
 */
const breakPaletteColor = (
  colorFamily: EColorNames,
  index: number,
  total: number,
  reverse: boolean
): string => {
  const scale = getColorScaleMap(colorFamily, Math.max(total, 1)) ?? [];
  const position = reverse ? total - 1 - index : index;
  const key = scale[position] ?? scale[reverse ? 0 : scale.length - 1];
  return COLORS[colorFamily]?.[key] ?? CUSTOM_BREAKS_OTHER_OUTLINE_COLOR;
};

/**
 * Fill and Outline for a break row, ramped in OPPOSITE directions from MIN→MAX:
 * Fill goes light→dark (matching Quantile — higher value darker), while Stroke
 * goes dark→light. So each row's fill and outline are contrasting shades.
 */
const breakRowColors = (
  colorFamily: EColorNames,
  index: number,
  total: number
): { fillColor: string; outlineColor: string } => ({
  fillColor: breakPaletteColor(colorFamily, index, total, true),
  outlineColor: breakPaletteColor(colorFamily, index, total, false)
});

/**
 * Formats a break boundary for display with thousands separators (e.g.
 * `8,117,993`) — same style as the data table/legend. Integers show no decimals;
 * decimals show up to 3 places. Falls back to the raw string if not formattable.
 */
export const formatBreakValue = (value: number): string => {
  const formatted = formatNumber(value, "decimal");
  return typeof formatted === "string" ? formatted : String(value);
};

/**
 * The numeric range text for a Custom Breaks step (ignores any user label).
 * Open-ended rows read "Less - X" / "Y - More"; middle rows read "X - Y". Values
 * are display-formatted with thousands separators.
 */
export const getCustomBreakRangeText = (
  step: Pick<CustomBreakStep, "start" | "end">
): string => {
  const start = step.start === null ? "Less" : formatBreakValue(step.start);
  const end = step.end === null ? "More" : formatBreakValue(step.end);
  return `${start} - ${end}`;
};

/**
 * The display label for a Custom Breaks range (legend + panel single-line): the
 * user label when set, otherwise the numeric range text. The panel shows BOTH
 * (label above, range below) when a label is set — see `CustomBreaksConfig`.
 */
export const getCustomBreakRangeLabel = (
  step: Pick<CustomBreakStep, "label" | "start" | "end">
): string =>
  step.label?.trim() ? step.label.trim() : getCustomBreakRangeText(step);

/**
 * Custom Breaks Fill-panel derivations: whether Custom Breaks is active, its own
 * step count (= break-row count, independent of `colorSteps`), and the dataset's numeric range.
 */
export const getCustomBreaksFillConfig = ({
  data,
  isCustomMapVisualizationLegendEnabled,
  stepValue,
  getAttributeNumericRange
}: {
  data: Dataset;
  isCustomMapVisualizationLegendEnabled: boolean;
  stepValue?: number;
  getAttributeNumericRange: (
    datasetId: string
  ) => { min: number; max: number } | undefined;
}): {
  isCustomBreaksFill: boolean;
  customBreaksStepCount: number;
  customBreaksRange: { min: number; max: number } | undefined;
} => {
  const isCustomBreaksFill =
    isCustomMapVisualizationLegendEnabled &&
    data.style.colorScaleMethod === "custom_breaks";

  const customBreaksStepCount =
    data.style.customBreaksConfig?.steps.length ??
    stepValue ??
    data.style.colorSteps;

  const customBreaksRange = isCustomBreaksFill
    ? getAttributeNumericRange(data.data.id)
    : undefined;

  return { isCustomBreaksFill, customBreaksStepCount, customBreaksRange };
};

/**
 * Value + change handler for the Fill panel's step input, following the
 * active color scale method; shared by `DatasetItem` and `MapPreviewMenu`.
 */
export const getFillStepInputConfig = ({
  data,
  isCustomMapVisualizationLegendEnabled,
  stepValue,
  setStepValue,
  getAttributeNumericRange,
  onChangeCustomOrdinalStepCount,
  onChangeCustomBreaksStepCount,
  onDefaultStepChange
}: {
  data: Dataset;
  isCustomMapVisualizationLegendEnabled: boolean;
  stepValue?: number;
  setStepValue: (step: number) => void;
  getAttributeNumericRange: (
    datasetId: string
  ) => { min: number; max: number } | undefined;
  onChangeCustomOrdinalStepCount: (datasetId: string, step: number) => void;
  onChangeCustomBreaksStepCount: (datasetId: string, step: number) => void;
  onDefaultStepChange: (step: number) => void;
}): {
  fillStepConfig: { step: number; onStepChange: (step: number) => void };
  customBreaksRange: { min: number; max: number } | undefined;
} => {
  const { isCustomBreaksFill, customBreaksStepCount, customBreaksRange } =
    getCustomBreaksFillConfig({
      data,
      isCustomMapVisualizationLegendEnabled,
      stepValue,
      getAttributeNumericRange
    });

  const isCustomOrdinalFill =
    isCustomMapVisualizationLegendEnabled &&
    data.style.colorScaleMethod === "custom_ordinal";

  if (isCustomOrdinalFill) {
    return {
      fillStepConfig: {
        step: stepValue ?? data.style.colorSteps,
        onStepChange: (step: number) => {
          if (step >= MIN_STEP && step <= MAX_STEP) {
            // Keep the input in sync (local override) AND resize the config.
            setStepValue(step);
            onChangeCustomOrdinalStepCount(data.data.id, step);
          }
        }
      },
      customBreaksRange
    };
  }

  if (isCustomBreaksFill) {
    return {
      fillStepConfig: {
        step: customBreaksStepCount,
        onStepChange: (step: number) => {
          if (step >= MIN_STEP && step <= MAX_STEP) {
            onChangeCustomBreaksStepCount(data.data.id, step);
          }
        }
      },
      customBreaksRange
    };
  }

  return {
    fillStepConfig: {
      step: stepValue ?? data.style.colorSteps,
      onStepChange: onDefaultStepChange
    },
    customBreaksRange
  };
};

/**
 * Distributes `count` breaks evenly across [min, max] using equal interval
 * (PRD): `interval = (max − min) / count`, boundary[i] = `min + i × interval`.
 * The first row is open-ended "Less" (start `null`), the last "More" (end
 * `null`); Fill ramps light→dark and Stroke dark→light from MIN to MAX (see
 * `breakRowColors`). Break values are always recalculated from the dataset's
 * actual MIN/MAX, never from prior manual edits. For integer attributes the
 * boundaries are whole numbers (no decimals).
 */
export const redistributeCustomBreaks = (
  count: number,
  min: number,
  max: number,
  colorFamily: EColorNames,
  isInteger: boolean = false
): CustomBreakStep[] => {
  const total = Math.min(
    Math.max(count, CUSTOM_BREAKS_MIN_STEPS),
    CUSTOM_BREAKS_MAX_STEPS
  );
  const interval = (max - min) / total;
  const boundary = (i: number) =>
    roundBreakValue(min + i * interval, isInteger);
  return Array.from({ length: total }, (_, index) => {
    const start = index === 0 ? null : boundary(index);
    const end = index === total - 1 ? null : boundary(index + 1);
    const { fillColor, outlineColor } = breakRowColors(
      colorFamily,
      index,
      total
    );
    return {
      label: "",
      start,
      end,
      fillColor,
      outlineColor,
      imageUrl: null,
      imageName: null,
      imageAssetId: null,
      iconId: null
    };
  });
};

/**
 * The default "Custom Breaks" config seeded when a layer first switches to the
 * scale: `count` equal-interval ranges across the dataset MIN/MAX plus the
 * catch-all "Other" bucket. Inherited step count comes from the previous Color
 * Scale (Quantile) setting (PRD).
 */
export const getDefaultCustomBreaksConfig = (
  colorFamily: EColorNames,
  count: number,
  min: number,
  max: number,
  isInteger: boolean = false
): CustomBreaksValue => ({
  steps: redistributeCustomBreaks(count, min, max, colorFamily, isInteger),
  otherFillColor: CUSTOM_BREAKS_OTHER_FILL_COLOR,
  otherOutlineColor: CUSTOM_BREAKS_OTHER_OUTLINE_COLOR
});

/**
 * Recolors a Custom Breaks config's fill/outline from a new Color Scale palette
 * — Fill light→dark and Stroke dark→light from MIN to MAX (see `breakRowColors`)
 * — WITHOUT touching the break values, labels or images. Used on a palette change
 * while Custom Breaks is active (unlike a step-count change, which redistributes
 * the values from scratch).
 */
export const recolorCustomBreaksConfig = (
  config: CustomBreaksValue,
  colorFamily: EColorNames
): CustomBreaksValue => {
  const total = config.steps.length || CUSTOM_BREAKS_MIN_STEPS;
  return {
    ...config,
    steps: config.steps.map((step, index) => ({
      ...step,
      ...breakRowColors(colorFamily, index, total)
    }))
  };
};

/** Whether a value falls inside a break range: `> start` (or any) and `≤ end`. */
export const isValueInBreak = (
  value: number,
  step: Pick<CustomBreakStep, "start" | "end">
): boolean =>
  (step.start === null || value > step.start) &&
  (step.end === null || value <= step.end);

/**
 * Numeric [min, max] of a set of raw values (strings/numbers), skipping empty /
 * non-numeric entries. Returns `null` when there is no usable numeric spread.
 */
export const getNumericRange = (
  values: (number | string | null | undefined)[]
): { min: number; max: number } | null => {
  let min = Infinity;
  let max = -Infinity;
  values.forEach((raw) => {
    if (raw === null || raw === undefined || raw === "") return;
    const n = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(n)) return;
    if (n < min) min = n;
    if (n > max) max = n;
  });
  return Number.isFinite(min) && Number.isFinite(max) && max > min
    ? { min, max }
    : null;
};

/**
 * Coerces a color-range bound to a finite number. Quantile ranges may store
 * `min`/`max` as formatted STRINGS (e.g. the last range's `min` from a quantile
 * boundary), so a strict `typeof === "number"` check drops them — coerce instead.
 * Returns `null` for empty / null / non-numeric.
 */
const toFiniteBound = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Falls back to the numeric spread already computed for the Quantile color
 * ranges when live feature values aren't available — the smallest `min` and the
 * largest `max` across the numeric ranges bound the dataset. Values are coerced
 * from strings, so string-formatted quantile bounds still resolve.
 */
export const getColorRangesMinMax = (
  colorRanges: ColorRanges[]
): { min: number; max: number } | null => {
  const mins: number[] = [];
  const maxes: number[] = [];
  colorRanges.forEach((range) => {
    const min = toFiniteBound(range.min);
    const max = toFiniteBound(range.max);
    if (min !== null && max !== null) {
      mins.push(min);
      maxes.push(max);
    }
  });
  if (!mins.length) return null;
  const min = Math.min(...mins);
  const max = Math.max(...maxes);
  return max > min ? { min, max } : null;
};

export const generateFilterableAttributes = <T extends DatasetDataColumn>(
  columns: T[] | undefined | null
) => {
  if (!columns || columns.length === 0) {
    return [];
  }

  return columns
    .filter((col) => col.is_visible)
    .map((col) => ({
      label: col.name,
      value: normalizeFilterValue(col.name),
      type: col.data_type,
      code: normalizeFilterValue(col.code)
    }));
};

export const formatAddAndUpdateDatasetRow = (
  data: Record<string, unknown>,
  metadata?: DatasetMetadata
): AddUpdateRowsDatasetPayload[] => {
  if (!metadata) return [];

  const allowedNames = new Set(metadata.schema.map((m) => m.code));

  const mappedData = Object.entries(data).map(([key, value]) => {
    const schema = metadata.schema.find((s) => s.code === key);
    // Handle date/time types
    if (
      schema?.data_type === DataSchemaType.DATE ||
      schema?.data_type === DataSchemaType.DATE_TIME ||
      schema?.data_type === DataSchemaType.TIME
    ) {
      const rawValue = value as string;
      return {
        code: key,
        name: key,
        // An empty optional date/time field must be sent as "" (the column is
        // nullable). Formatting an empty value with dayjs yields "Invalid Date",
        // so short-circuit before converting.
        value: rawValue
          ? convertFormatterDayjs(
              rawValue,
              schema?.time_format || "",
              schema.data_type
            )
          : ""
      };
    }

    // Handle geometry types
    if (schema?.data_type === DataSchemaType.GEOMETRY) {
      const format = data[`format_geom_${key}`];

      if (format === "coordinates") {
        const latitude = data[`latitude_geom_input_${key}`];
        const longitude = data[`longitude_geom_input_${key}`];
        return {
          code: key,
          name: key,
          value: `POINT(${longitude} ${latitude})`
        };
      }

      if (format === "wkt") {
        return {
          code: key,
          name: key,
          value: data[`wkt_geom_input_${key}`] as string
        };
      }

      if (format === "geojson") {
        return {
          code: key,
          name: key,
          value: data[`geojson_geom_input_${key}`] as string
        };
      }

      if (format === "draw_polygon") {
        return {
          code: key,
          name: key,
          value: data[`draw_polygon_${key}`] as string
        };
      }

      return {
        code: key,
        name: key,
        value: value as string
      };
    }

    // Default (stringify all other types)
    return {
      code: key,
      name: key,
      value: String(value)
    };
  });

  return mappedData.filter((item) => allowedNames.has(item.code));
};

/**
 * Creates dataset attributes from column data
 * @param columns - Array of column data from dataset
 * @returns Array of DatasetAttributeSchema objects
 */
export const createDatasetAttributes = (
  columns: DatasetData["columns"]
): DatasetAttributeSchema[] => {
  return columns.map((column, index) => ({
    ...DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE,
    id: index.toString(),
    code: column.code,
    attribute_name: column.name,
    data_type: DATA_TYPE_VALUES[column.data_type],
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    position: index,
    is_visible: column?.is_visible ?? false,
    data_label: column?.data_label ?? undefined
  }));
};

export const createNewDataset = ({
  dataset,
  datasetId,
  datasetName,
  attributesSchema,
  h3Configuration = null,
  styleConfig,
  totalRows = 0,
  index,
  group_by,
  is_global_admin_area = false,
  showCatchmentPopup = false
}: CreateNewDatasetType): Dataset => {
  const defaultCategory = getDefaultSelectedCategory(
    attributesSchema,
    dataset.spatial_aggregation_type
  );
  const colors =
    EColorNames[ALL_COLORS[index || 0] as keyof typeof EColorNames];
  const defaultColorSteps = computeStepValue(totalRows);
  // Default Color Scale method: POI datasets default to "Simple"; other layers
  // derive from the attribute data type (ordinal vs quantile). An explicit
  // `styleConfig.colorScaleMethod` (e.g. a restored/saved value) still wins.
  const derivedColorScaleMethod: ColorScaleMethod =
    dataset.spatial_aggregation_type === "poi"
      ? "simple"
      : getDefaultColorScaleMethod(defaultCategory?.data_type);
  const colorScaleMethod =
    styleConfig?.colorScaleMethod ?? derivedColorScaleMethod;

  // First default color family per color based on
  const defaultColorFamily = styleConfig?.colorFamily ?? colors;
  const colorScaleColorFamily: Partial<Record<ColorScaleMethod, EColorNames>> =
    {
      simple: defaultColorFamily,
      ordinal: defaultColorFamily,
      quantile: defaultColorFamily,
      custom_ordinal: defaultColorFamily,
      custom_breaks: defaultColorFamily
    };
  // Prefer a restored config (from a saved project/analysis config); otherwise
  // seed a default Simple config so a new POI layer renders on the map/legend
  // immediately (matching the config panel default) without needing a Save.
  const simpleConfig: SimpleFillValue | undefined =
    styleConfig?.simpleConfig ??
    (colorScaleMethod === "simple"
      ? getDefaultSimpleConfig(defaultCategory?.attribute_name ?? "")
      : undefined);
  const isCatchmentPolygon =
    showCatchmentPopup && dataset.spatial_aggregation_type === "polygon";
  return {
    data: {
      ...dataset,
      id: datasetId,
      name: dataset.name,
      display_name: datasetName,
      schema: dataset.schema,
      available: dataset.available ?? true
    },
    properties: {
      isVisible: dataset.available ?? true,
      spatialType: dataset.spatial_aggregation_type
    },
    style: {
      weight: 1,
      opacity: 0.7,
      color: "#fff",
      fillColor: COLORS[defaultColorFamily][500],
      colorFamily: defaultColorFamily,
      colorScaleColorFamily,
      fillOpacity: styleConfig?.opacity ?? 0.7,
      visibility: "visible",
      colorRanges: [],
      labelConfig: {
        color_scale:
          styleConfig?.labelConfig?.color_scale ?? EColorLabel.BLACK_WHITE,
        attribute_id:
          styleConfig?.labelConfig?.attribute_id ?? defaultCategory?.id ?? "",
        attribute_name:
          attributesSchema.find(
            (item) => item.id === styleConfig?.labelConfig?.attribute_id
          )?.attribute_name ??
          defaultCategory?.attribute_name ??
          "",
        show_label:
          (styleConfig?.labelConfig?.show_label || isCatchmentPolygon) ?? false,
        opacity: styleConfig?.labelConfig?.opacity ?? 0.7
      },
      rankConfig: {
        isEnabled: styleConfig?.rankConfig?.isEnabled ?? false,
        maxRanking: styleConfig?.rankConfig?.maxRanking ?? 10
      },
      stroke: {
        attribute_id:
          styleConfig?.strokeConfig?.attribute_id ?? defaultCategory?.id ?? "",
        attribute_name:
          styleConfig?.strokeConfig?.attribute_name ??
          defaultCategory?.attribute_name ??
          "",
        width: styleConfig?.strokeConfig?.width ?? 1,
        opacity: styleConfig?.strokeConfig?.opacity ?? 0.7
      },
      colorSteps: styleConfig?.colorSteps ?? defaultColorSteps,
      colorScaleMethod,
      // A restored config is by definition user-saved, so it hydrates BOTH the
      // live and saved slots (the saved slot is the Cancel baseline + "confirmed"
      // marker). A fresh dataset seeds these lazily on method switch.
      simpleConfig,
      simpleConfigSaved: simpleConfig,
      // Restored "Custom Ordinal" config, when opening a saved config; a fresh
      // dataset seeds this lazily when the user switches to the method. A restored
      // config hydrates BOTH slots (the saved slot is the Cancel baseline).
      customOrdinalConfig: styleConfig?.customOrdinalConfig,
      customOrdinalConfigSaved: styleConfig?.customOrdinalConfig,
      // Restored "Custom Breaks" config, when opening a saved config; a fresh
      // dataset seeds this lazily when the user switches to the method. A restored
      // config hydrates BOTH slots (the saved slot is the Cancel baseline).
      customBreaksConfig: styleConfig?.customBreaksConfig,
      customBreaksConfigSaved: styleConfig?.customBreaksConfig
    },
    categories: attributesSchema,
    selectedCategory:
      styleConfig?.selectedCategory ??
      defaultCategory ??
      DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE,
    h3Configuration,
    group_by,
    is_global_admin_area
  };
};

export function buildGridConfig(h3Configuration?: H3Configuration | null) {
  if (!h3Configuration) return {};

  return {
    grid_config: {
      level: h3Configuration.resolution
        ? Number(h3Configuration.resolution)
        : undefined,
      aggregation_type: h3Configuration.aggregationType
    }
  };
}

export function sumPolygonDatasetArea(
  datasets: CatchmentDatasetConfig["datasets"]
): number {
  let totalArea = 0;
  for (const entry of Object.values(datasets)) {
    if (entry.dataset?.data?.spatial_aggregation_type !== "polygon") continue;
    for (const feature of convertDatasetResponseToFeatureCollection(
      entry.rawDataset
    ).features) {
      if (
        feature.geometry?.type === "Polygon" ||
        feature.geometry?.type === "MultiPolygon"
      ) {
        totalArea += area(feature);
      }
    }
  }
  return totalArea;
}

/**
 * Color conversion helpers shared by the Color Picker panel (Hex/RGB/HSL/HSB
 * tabs). All hex values are `#rrggbb` lowercase. HSL/HSV use degrees (0-360)
 * for hue and percentages (0-100) for the remaining channels.
 */

export type Rgb = { r: number; g: number; b: number };
export type Hsl = { h: number; s: number; l: number };
/** HSB is the same model as HSV (Brightness === Value). */
export type Hsv = { h: number; s: number; v: number };

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

/** Normalize a hex string to a `#rrggbb` lowercase form (expands shorthand). */
export const normalizeHex = (hex: string): string => {
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  return `#${h.toLowerCase()}`;
};

export const hexToRgb = (hex: string): Rgb | null => {
  const h = normalizeHex(hex).replace(/^#/, "");
  if (h.length !== 6 || /[^0-9a-f]/.test(h)) return null;
  const num = Number.parseInt(h, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};

export const rgbToHex = ({ r, g, b }: Rgb): string => {
  const toHex = (v: number) =>
    clamp(Math.round(v || 0), 0, 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const rgbToHsl = ({ r, g, b }: Rgb): Hsl => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rn:
        h = ((gn - bn) / delta) % 6;
        break;
      case gn:
        h = (bn - rn) / delta + 2;
        break;
      default:
        h = (rn - gn) / delta + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
};

export const hslToRgb = ({ h, s, l }: Hsl): Rgb => {
  const sn = clamp(s, 0, 100) / 100;
  const ln = clamp(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = ln - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (hp >= 0 && hp < 1) [r, g, b] = [c, x, 0];
  else if (hp < 2) [r, g, b] = [x, c, 0];
  else if (hp < 3) [r, g, b] = [0, c, x];
  else if (hp < 4) [r, g, b] = [0, x, c];
  else if (hp < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255)
  };
};

export const rgbToHsv = ({ r, g, b }: Rgb): Hsv => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    switch (max) {
      case rn:
        h = ((gn - bn) / delta) % 6;
        break;
      case gn:
        h = (bn - rn) / delta + 2;
        break;
      default:
        h = (rn - gn) / delta + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : delta / max;
  return { h: Math.round(h), s: Math.round(s * 100), v: Math.round(max * 100) };
};

export const hsvToRgb = ({ h, s, v }: Hsv): Rgb => {
  const sn = clamp(s, 0, 100) / 100;
  const vn = clamp(v, 0, 100) / 100;
  const c = vn * sn;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = vn - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (hp >= 0 && hp < 1) [r, g, b] = [c, x, 0];
  else if (hp < 2) [r, g, b] = [x, c, 0];
  else if (hp < 3) [r, g, b] = [0, c, x];
  else if (hp < 4) [r, g, b] = [0, x, c];
  else if (hp < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255)
  };
};
